import { fetchWithAuth } from "../auth/googleAuth";
import {
  copyFile,
  deleteFile,
  ensureFormsFolder,
  findFileInFolder,
  listFilesInFolder,
  moveFileToFolder,
  renameFile,
} from "./drive";
import {
  CHOICE_FORM_TYPES,
  ID_LABEL,
  QUESTION_FORM_TYPES,
  SINGLETON_FORM_TYPES,
  SPREADSHEET_NAME,
  findIdFieldIndex,
  readFormFields,
  readQrSettings,
  writeQrSettings,
} from "./sheets";
import { FormField, QrSettings } from "../types/sheets";

const FORMS_BASE = "https://forms.googleapis.com/v1/forms";
const TEMPLATE_FORM_NAME = "Template";

export class FormSyncError extends Error {
  code: string;
  constructor(code: string, message: string) {
    super(message);
    this.code = code;
  }
}

export function formEditUrl(formId: string): string {
  return `https://docs.google.com/forms/d/${formId}/edit`;
}

export function parseFormIdFromUrl(rawUrl: unknown): string {
  const url = String(rawUrl || "").trim();
  if (!url) return "";
  const m = url.match(/\/forms\/d\/(?:e\/)?([a-zA-Z0-9_-]+)/);
  return m ? m[1] : "";
}

function parseEntryIdFromUrl(rawUrl: unknown): string {
  const m = String(rawUrl || "").match(/[?&]entry\.([a-zA-Z0-9]+)=/);
  return m ? m[1] : "";
}

function entryIdForQuestion(questionId: unknown): string {
  const n = parseInt(String(questionId || ""), 16);
  return Number.isSafeInteger(n) ? String(n) : "";
}

function validateImportUrl(url: unknown): string {
  const trimmed = String(url || "").trim();
  if (!trimmed) throw new FormSyncError("URL_EMPTY", "URL is empty");
  if (!/^https?:\/\//i.test(trimmed)) throw new FormSyncError("URL_INVALID", "URL must start with http(s)://");
  if (!/\/forms\/d\//.test(trimmed)) throw new FormSyncError("URL_INVALID", "URL doesn't look like a Google Forms URL");
  if (!/[?&]entry\.[a-zA-Z0-9]+=/.test(trimmed)) {
    throw new FormSyncError("URL_NO_ENTRY", "URL must contain a prefilled entry parameter ending in &entry.<number>=");
  }
  return trimmed;
}

interface GoogleForm {
  formId?: string;
  responderUri?: string;
  linkedSheetId?: string;
  info?: { title?: string; description?: string };
  items?: GoogleFormItem[];
}

interface GoogleFormItem {
  title?: string;
  description?: string;
  questionItem?: { question?: { questionId?: string; textQuestion?: unknown; choiceQuestion?: unknown } };
  textItem?: unknown;
}

async function apiCreateForm(title: string): Promise<{ formId: string; responderUri: string }> {
  const res = await fetchWithAuth(FORMS_BASE, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ info: { title, documentTitle: title } }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Forms create failed (${res.status}): ${text}`);
  }
  const data = await res.json() as GoogleForm;
  return { formId: data.formId!, responderUri: data.responderUri! };
}

async function apiGetForm(formId: string): Promise<GoogleForm> {
  const res = await fetchWithAuth(`${FORMS_BASE}/${formId}`);
  if (!res.ok) throw new Error(`Forms read failed (${res.status})`);
  return res.json() as Promise<GoogleForm>;
}

async function apiGetFormOrNull(formId: string): Promise<GoogleForm | null> {
  const res = await fetchWithAuth(`${FORMS_BASE}/${formId}`);
  if (res.status >= 400 && res.status < 500) return null;
  if (!res.ok) throw new Error(`Forms read failed (${res.status})`);
  return res.json() as Promise<GoogleForm>;
}

function buildQuestionItem(field: FormField): object {
  switch (field.type) {
    case "textarea":
      return { question: { textQuestion: { paragraph: true } } };
    case "select":
      return { question: { choiceQuestion: { type: "DROP_DOWN", options: field.values.map((v) => ({ value: v })) } } };
    case "radio":
      return { question: { choiceQuestion: { type: "RADIO", options: field.values.map((v) => ({ value: v })) } } };
    case "checkbox":
      return { question: { choiceQuestion: { type: "CHECKBOX", options: field.values.map((v) => ({ value: v })) } } };
    case "text":
    default:
      return { question: { textQuestion: { paragraph: false } } };
  }
}

function buildItemFromField(field: FormField): Record<string, unknown> {
  const item: Record<string, unknown> = { title: field.label };
  if (field.type === "title") {
    item.textItem = {};
  } else {
    item.questionItem = buildQuestionItem(field);
  }
  if (field.description && field.description.trim()) {
    item.description = field.description;
  }
  return item;
}

async function apiBatchUpdate(formId: string, requests: unknown[]): Promise<unknown> {
  if (requests.length === 0) return null;
  const res = await fetchWithAuth(`${FORMS_BASE}/${formId}:batchUpdate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ requests }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`Forms batchUpdate failed (${res.status}): ${text}`);
  }
  return res.json();
}

function prefilledUrl(responderUri: string, entryId: string): string {
  return `${responderUri}?usp=pp_url&entry.${entryId}=`;
}

interface SchemaInfo {
  title: string;
  description: string;
  itemFields: FormField[];
  idIndex: number;
}

function classifySchema(fields: FormField[]): SchemaInfo {
  if (fields.length === 0 || fields[0].type !== "title") {
    throw new FormSyncError("TITLE_FIRST_REQUIRED", "The first row must be of type \"title\"");
  }
  const headRow = fields[0];
  const itemFields = fields.slice(1).filter(
    (f) => QUESTION_FORM_TYPES.includes(f.type) || f.type === "title"
  );
  const questionFields = itemFields.filter((f) => QUESTION_FORM_TYPES.includes(f.type));
  if (questionFields.length === 0) {
    throw new FormSyncError("NO_QUESTIONS", "Schema must contain at least one question row");
  }
  const idQuestionIndex = findIdFieldIndex(questionFields);
  if (idQuestionIndex < 0) {
    throw new FormSyncError("ID_FIELD_MISSING", "Schema must contain a question row labelled \"ID\"");
  }
  const idIndex = itemFields.indexOf(questionFields[idQuestionIndex]);
  for (const f of itemFields) {
    if (CHOICE_FORM_TYPES.includes(f.type) && f.values.length === 0) {
      throw new FormSyncError("CHOICE_VALUES_MISSING", `Field "${f.label}" (${f.type}) needs at least one value`);
    }
  }
  return {
    title: headRow.label?.trim() || "",
    description: headRow.description?.trim() || "",
    itemFields,
    idIndex,
  };
}

export async function ensureFormTemplate(): Promise<{ formId: string; created: boolean }> {
  const folderId = await ensureFormsFolder();
  const existing = await findFileInFolder(folderId, TEMPLATE_FORM_NAME);
  if (existing && existing.mimeType === "application/vnd.google-apps.form") {
    return { formId: existing.id, created: false };
  }
  const { formId } = await apiCreateForm(TEMPLATE_FORM_NAME);
  try { await moveFileToFolder(formId, folderId); } catch (e) { console.warn("Could not move template form:", e); }
  try { await renameFile(formId, TEMPLATE_FORM_NAME); } catch (e) { console.warn("Could not rename template form:", e); }
  return { formId, created: true };
}

// Adopts a Picker-selected Google Form as the template seed that future forms copy.
// The selected file may be owned by another account and shared publicly: the Picker
// grant gives our token enough access to copy it, and the resulting copy is
// app-created, so we retain full drive.file access to it afterwards. The copy keeps
// the source's theme/background, which the Forms API cannot set itself — that is the
// whole reason for going through the Picker rather than rebuilding from schema.
export async function setThemedTemplateFromPicked(fileId: string): Promise<{ formId: string }> {
  if (!fileId) throw new FormSyncError("PICK_EMPTY", "No file was selected");
  const folderId = await ensureFormsFolder();
  // Remove any existing template first so ensureFormTemplate resolves the new one
  // unambiguously (it matches by the "Template" name).
  const existing = await findFileInFolder(folderId, TEMPLATE_FORM_NAME);
  if (existing) {
    try { await deleteFile(existing.id); } catch (e) { console.warn("Could not remove old template:", e); }
  }
  const copied = await copyFile(fileId, { name: TEMPLATE_FORM_NAME, parents: [folderId] });
  const live = await apiGetFormOrNull(copied.id);
  if (!live) {
    throw new FormSyncError("TEMPLATE_NOT_FORM", "The selected file could not be read as a Google Form.");
  }
  return { formId: copied.id };
}

async function createFormFromSchema(schemaInfo: SchemaInfo): Promise<{ formId: string; formUrl: string }> {
  const initialTitle = schemaInfo.title || `${SPREADSHEET_NAME} — Report`;
  const { formId: templateId } = await ensureFormTemplate();
  const folderId = await ensureFormsFolder();
  const copied = await copyFile(templateId, { name: initialTitle, parents: [folderId] });
  const formId = copied.id;

  const requests: unknown[] = [];
  const live = await apiGetForm(formId);
  const liveItems = live.items || [];
  for (let i = liveItems.length - 1; i >= 0; i--) {
    requests.push({ deleteItem: { location: { index: i } } });
  }
  requests.push({
    updateFormInfo: {
      info: { title: initialTitle, description: schemaInfo.description || "" },
      updateMask: "title,description",
    },
  });
  schemaInfo.itemFields.forEach((f, i) => {
    requests.push({ createItem: { item: buildItemFromField(f), location: { index: i } } });
  });
  await apiBatchUpdate(formId, requests);

  const after = await apiGetForm(formId);
  const responderUri = after.responderUri!;
  const items = after.items || [];
  const questionId = items[schemaInfo.idIndex]?.questionItem?.question?.questionId;
  const entryId = entryIdForQuestion(questionId);
  if (!entryId) {
    throw new FormSyncError("ENTRY_ID_MISSING", "Could not resolve the ID field's entry ID after form creation");
  }
  return { formId, formUrl: prefilledUrl(responderUri, entryId) };
}

async function rebuildExistingForm(
  formId: string,
  liveForm: GoogleForm,
  knownEntryId: string,
  schemaInfo: SchemaInfo
): Promise<{ formUrl: string } | null> {
  const liveItems = liveForm.items || [];
  let preservedLiveIdx = -1;
  if (knownEntryId) {
    preservedLiveIdx = liveItems.findIndex((it) => {
      const qid = it?.questionItem?.question?.questionId;
      return Boolean(qid) && (qid === knownEntryId || entryIdForQuestion(qid) === knownEntryId);
    });
  }
  if (preservedLiveIdx < 0) return null;

  const idField = schemaInfo.itemFields[schemaInfo.idIndex];
  const preservedQid = liveItems[preservedLiveIdx]?.questionItem?.question?.questionId;

  const pool = new Map<string, number[]>();
  for (let i = 0; i < liveItems.length; i++) {
    if (i === preservedLiveIdx) continue;
    const title = String(liveItems[i]?.title ?? "").trim();
    if (!pool.has(title)) pool.set(title, []);
    pool.get(title)!.push(i);
  }

  const updates: Array<{ item: Record<string, unknown>; location: { index: number }; updateMask: string }> = [];
  const creates: FormField[] = [];
  const consumed = new Set<number>();
  schemaInfo.itemFields.forEach((field, i) => {
    if (i === schemaInfo.idIndex) {
      updates.push({
        item: { title: idField.label, description: idField.description || "" },
        location: { index: preservedLiveIdx },
        updateMask: "title,description",
      });
      return;
    }
    const title = String(field.label ?? "").trim();
    const queue = pool.get(title);
    if (queue && queue.length) {
      const liveIdx = queue.shift()!;
      consumed.add(liveIdx);
      const item = buildItemFromField(field);
      if (item.description === undefined) item.description = "";
      updates.push({
        item,
        location: { index: liveIdx },
        updateMask: field.type === "title" ? "title,description" : "title,description,questionItem",
      });
    } else {
      creates.push(field);
    }
  });

  const deletions: number[] = [];
  for (let i = 0; i < liveItems.length; i++) {
    if (i === preservedLiveIdx || consumed.has(i)) continue;
    deletions.push(i);
  }

  const phase1: unknown[] = [
    {
      updateFormInfo: {
        info: { title: schemaInfo.title || liveForm.info?.title || "", description: schemaInfo.description },
        updateMask: "title,description",
      },
    },
  ];
  for (const u of updates) phase1.push({ updateItem: u });
  for (const idx of [...deletions].sort((a, b) => b - a)) {
    phase1.push({ deleteItem: { location: { index: idx } } });
  }
  let appendAt = liveItems.length - deletions.length;
  for (const field of creates) {
    phase1.push({ createItem: { item: buildItemFromField(field), location: { index: appendAt } } });
    appendAt++;
  }
  await apiBatchUpdate(formId, phase1);

  const after = await apiGetForm(formId);
  const afterItems = after.items || [];
  const ID_KEY = " ID ";
  const isPreservedId = (it: GoogleFormItem) => {
    const qid = it?.questionItem?.question?.questionId;
    return Boolean(qid) &&
      (qid === preservedQid || qid === knownEntryId || entryIdForQuestion(qid) === knownEntryId);
  };
  const desired = schemaInfo.itemFields.map((field, i) =>
    i === schemaInfo.idIndex ? ID_KEY : String(field.label ?? "").trim()
  );
  const current = afterItems.map((it) =>
    isPreservedId(it) ? ID_KEY : String(it?.title ?? "").trim()
  );
  const moves: unknown[] = [];
  for (let i = 0; i < desired.length && i < current.length; i++) {
    if (current[i] === desired[i]) continue;
    let j = -1;
    for (let k = i + 1; k < current.length; k++) {
      if (current[k] === desired[i]) { j = k; break; }
    }
    if (j < 0) continue;
    moves.push({ moveItem: { originalLocation: { index: j }, newLocation: { index: i } } });
    const [moved] = current.splice(j, 1);
    current.splice(i, 0, moved);
  }
  if (moves.length) await apiBatchUpdate(formId, moves);

  const entryId = entryIdForQuestion(preservedQid);
  const formUrl = entryId && liveForm.responderUri
    ? prefilledUrl(liveForm.responderUri, entryId)
    : "";
  return { formUrl };
}

export async function createFormFromSchemaAction(spreadsheetId: string): Promise<{ formId: string; formUrl: string; editUrl: string; created: boolean }> {
  const qr = await readQrSettings(spreadsheetId);
  if (qr.formId || qr.formUrl) {
    throw new FormSyncError("ALREADY_LINKED", "A Google Form is already linked. Detach or replace it before creating a new one.");
  }
  const fields = await readFormFields(spreadsheetId);
  const schemaInfo = classifySchema(fields);
  const { formId, formUrl } = await createFormFromSchema(schemaInfo);
  await writeQrSettings(spreadsheetId, { ...qr, formId, formUrl });
  return { formId, formUrl, editUrl: formEditUrl(formId), created: true };
}

export async function syncToForm(spreadsheetId: string): Promise<{ formId: string; formUrl: string; editUrl: string; created: boolean }> {
  const qr = await readQrSettings(spreadsheetId);
  if (!qr.formId) throw new FormSyncError("NOT_LINKED", "No form is linked. Use Create from schema first.");
  if (qr.formLocked) throw new FormSyncError("FORM_LOCKED", "This form is linked by ID for reuse and is never edited by MewMaps. Detach it first if you want a managed form.");

  const liveForm = await apiGetFormOrNull(qr.formId);
  if (!liveForm) throw new FormSyncError("FORM_INACCESSIBLE", "Cannot access the linked Google Form. MewMaps can only manage forms it created itself — a form imported by URL must be edited directly in Google Forms. Detach it and create a new form to enable automatic management.");

  const fields = await readFormFields(spreadsheetId);
  const schemaInfo = classifySchema(fields);
  const knownEntryId = parseEntryIdFromUrl(qr.formUrl);
  const rebuilt = await rebuildExistingForm(qr.formId, liveForm, knownEntryId, schemaInfo);
  if (!rebuilt) {
    throw new FormSyncError("ID_QUESTION_MISSING", "The linked form no longer contains the ID question. Detach and create a new form to fix this.");
  }
  const formUrl = rebuilt.formUrl || qr.formUrl;
  if (formUrl !== qr.formUrl) {
    await writeQrSettings(spreadsheetId, { ...qr, formUrl });
  }
  return { formId: qr.formId, formUrl, editUrl: formEditUrl(qr.formId), created: false };
}

export async function importExistingFormUrl(
  spreadsheetId: string,
  rawUrl: unknown,
  { confirmReplace = false } = {}
): Promise<{ formId: string; formUrl: string; editUrl: string }> {
  const url = validateImportUrl(rawUrl);
  const formId = parseFormIdFromUrl(url);
  if (!formId) throw new FormSyncError("URL_INVALID", "Could not extract form ID from URL");

  const qr = await readQrSettings(spreadsheetId);
  const hasExisting = Boolean(qr.formUrl || qr.formId);
  if (hasExisting && !confirmReplace) {
    throw new FormSyncError("WOULD_OVERWRITE", "An existing URL is set. Confirm explicitly to replace it.");
  }

  // A URL-imported form is not API-managed; clear the lock flag for it.
  const next: QrSettings = { ...qr, formId, formUrl: url, formLocked: false };
  if (hasExisting) {
    next.formUrlBackup = qr.formUrl;
    next.formIdBackup = qr.formId;
    next.formLockedBackup = qr.formLocked;
  }
  await writeQrSettings(spreadsheetId, next);
  return { formId, formUrl: url, editUrl: formEditUrl(formId) };
}

// Reuses an existing Google Form by ID (e.g. migrating from an older app whose QR codes
// already point at this form). The Picker grant lets us READ the form to build the
// prefilled URL, but the form is marked formLocked so MewMaps never edits it. We do not
// copy it and we do not require it to be in the MewMaps folder.
export async function linkExistingFormById(
  spreadsheetId: string,
  formId: string,
  { confirmReplace = false } = {}
): Promise<{ formId: string; formUrl: string; editUrl: string; hasIdQuestion: boolean }> {
  if (!formId) throw new FormSyncError("PICK_EMPTY", "No form was selected");
  const qr = await readQrSettings(spreadsheetId);
  const hasExisting = Boolean(qr.formId || qr.formUrl);
  if (hasExisting && !confirmReplace) {
    throw new FormSyncError("WOULD_OVERWRITE", "An existing form is linked. Confirm explicitly to replace it.");
  }
  const live = await apiGetFormOrNull(formId);
  if (!live) {
    throw new FormSyncError("FORM_INACCESSIBLE", "Could not read the selected form. Make sure you picked it via the chooser so the app is granted access.");
  }
  // Build the prefilled URL from the form's "ID" question (read-only). Fall back to the
  // plain responder URL if the form has no "ID" question.
  const target = ID_LABEL.toLowerCase();
  const idItem = (live.items || []).find(
    (it) => String(it?.title ?? "").trim().toLowerCase() === target && it?.questionItem
  );
  const entryId = entryIdForQuestion(idItem?.questionItem?.question?.questionId);
  const hasIdQuestion = Boolean(entryId);
  let formUrl = "";
  if (hasIdQuestion && live.responderUri) {
    formUrl = prefilledUrl(live.responderUri, entryId);
  } else if (live.responderUri) {
    formUrl = live.responderUri;
  }

  const next: QrSettings = { ...qr, formId, formUrl, formLocked: true };
  if (hasExisting) {
    next.formUrlBackup = qr.formUrl;
    next.formIdBackup = qr.formId;
    next.formLockedBackup = qr.formLocked;
  }
  await writeQrSettings(spreadsheetId, next);
  return { formId, formUrl, editUrl: formEditUrl(formId), hasIdQuestion };
}

export async function checkLinkedFormAccessible(spreadsheetId: string): Promise<{ formId: string; accessible: boolean; qr: QrSettings }> {
  const qr = await readQrSettings(spreadsheetId);
  if (!qr.formId) return { formId: "", accessible: true, qr };
  const live = await apiGetFormOrNull(qr.formId);
  return { formId: qr.formId, accessible: Boolean(live), qr };
}

export async function pruneLinkedForm(spreadsheetId: string): Promise<QrSettings> {
  const qr = await readQrSettings(spreadsheetId);
  if (!qr.formId && !qr.formUrl) return qr;
  const next: QrSettings = {
    ...qr,
    formId: "",
    formUrl: "",
    formLocked: false,
    formIdBackup: qr.formId,
    formUrlBackup: qr.formUrl,
    formLockedBackup: qr.formLocked,
  };
  await writeQrSettings(spreadsheetId, next);
  return next;
}

export async function detachForm(spreadsheetId: string): Promise<{ formIdBackup: string; formUrlBackup: string }> {
  const qr = await readQrSettings(spreadsheetId);
  if (!qr.formId && !qr.formUrl) throw new FormSyncError("NOT_LINKED", "No form is linked");
  await writeQrSettings(spreadsheetId, {
    ...qr,
    formId: "",
    formUrl: "",
    formLocked: false,
    formIdBackup: qr.formId,
    formUrlBackup: qr.formUrl,
    formLockedBackup: qr.formLocked,
  });
  return { formIdBackup: qr.formId, formUrlBackup: qr.formUrl };
}

export async function restoreFormUrlBackup(spreadsheetId: string): Promise<{ formId: string; formUrl: string; editUrl: string }> {
  const qr = await readQrSettings(spreadsheetId);
  if (!qr.formUrlBackup && !qr.formIdBackup) throw new FormSyncError("NO_BACKUP", "No backup to restore");
  const next: QrSettings = {
    ...qr,
    formUrl: qr.formUrlBackup,
    formId: qr.formIdBackup,
    formLocked: qr.formLockedBackup,
    formUrlBackup: qr.formUrl,
    formIdBackup: qr.formId,
    formLockedBackup: qr.formLocked,
  };
  await writeQrSettings(spreadsheetId, next);
  return { formId: next.formId, formUrl: next.formUrl, editUrl: next.formId ? formEditUrl(next.formId) : "" };
}

export async function linkFirstFormInFolder(spreadsheetId: string): Promise<{ formId: string; formUrl: string; name: string }> {
  const qr = await readQrSettings(spreadsheetId);
  if (qr.formId || qr.formUrl) {
    throw new FormSyncError("ALREADY_LINKED", "A Google Form is already linked. Detach or replace it before scanning.");
  }
  const folderId = await ensureFormsFolder();
  const files = await listFilesInFolder(folderId, { mimeType: "application/vnd.google-apps.form" });
  const candidate = files.find((f) => f.name !== TEMPLATE_FORM_NAME);
  if (!candidate) {
    throw new FormSyncError("NO_FORM_FOUND", "No Google Form found in the forms/ folder (excluding Template).");
  }
  const live = await apiGetFormOrNull(candidate.id);
  if (!live) throw new FormSyncError("FORM_INACCESSIBLE", `Found "${candidate.name}" but couldn't read it.`);
  const target = ID_LABEL.toLowerCase();
  const idItem = (live.items || []).find(
    (it) => String(it?.title ?? "").trim().toLowerCase() === target && it?.questionItem
  );
  const entryId = entryIdForQuestion(idItem?.questionItem?.question?.questionId);
  if (!entryId || !live.responderUri) {
    throw new FormSyncError("ENTRY_ID_MISSING", `Found "${candidate.name}" but it has no question titled "${ID_LABEL}".`);
  }
  const formUrl = prefilledUrl(live.responderUri, entryId);
  await writeQrSettings(spreadsheetId, { ...qr, formId: candidate.id, formUrl, formLocked: false });
  return { formId: candidate.id, formUrl, name: candidate.name };
}

async function verifyFileAccessible(fileId: string): Promise<boolean> {
  if (!fileId) return false;
  const res = await fetchWithAuth(
    `https://www.googleapis.com/drive/v3/files/${fileId}?fields=id,trashed`
  );
  if (!res.ok) return false;
  const data = await res.json() as { trashed?: boolean };
  return !data.trashed;
}

async function createResponsesSheet(folderId: string, title: string): Promise<string> {
  const createRes = await fetchWithAuth("https://sheets.googleapis.com/v4/spreadsheets", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ properties: { title } }),
  });
  if (!createRes.ok) {
    throw new FormSyncError("RESPONSES_SHEET_FAILED", `Responses sheet create failed (${createRes.status})`);
  }
  const created = await createRes.json() as { spreadsheetId: string };
  const sheetId = created.spreadsheetId;
  try { await moveFileToFolder(sheetId, folderId); } catch (e) { console.warn("moveFileToFolder responses sheet:", e); }
  return sheetId;
}

// Ensures a responses spreadsheet exists for the linked form and records its ID.
// MewMaps cannot attach it to the form (only Apps Script can call setDestination),
// so the user links the sheet to the form manually in the Forms UI.
export async function ensureResponsesSheet(spreadsheetId: string): Promise<{
  responsesSheetId: string;
  createdSheet: boolean;
  alreadyLinked: boolean;
}> {
  const qr = await readQrSettings(spreadsheetId);
  if (!qr.formId) throw new FormSyncError("NOT_LINKED", "No form is linked. Create or import one first.");
  const live = await apiGetFormOrNull(qr.formId);
  if (!live) throw new FormSyncError("FORM_INACCESSIBLE", "Cannot access the linked Google Form. MewMaps can only manage forms it created itself — a form imported by URL must be edited directly in Google Forms.");

  // A form reused by ID is never modified, and neither is its responses sheet. Adopt the
  // sheet the form is already attached to — linkedSheetId from the Forms API is
  // authoritative, and we don't need drive access to it (the email backend runs under the
  // user's own auth). We never create or move a sheet in this mode.
  if (qr.formLocked) {
    const linked = live.linkedSheetId || qr.responsesSheetId || "";
    if (linked !== qr.responsesSheetId) {
      await writeQrSettings(spreadsheetId, { ...qr, responsesSheetId: linked });
    }
    return { responsesSheetId: linked, createdSheet: false, alreadyLinked: Boolean(live.linkedSheetId) };
  }

  const formTitle = (live.info?.title || "Form").trim() || "Form";
  const folderId = await ensureFormsFolder();

  // If the user already linked a responses sheet via the Forms UI, adopt it.
  if (live.linkedSheetId && (await verifyFileAccessible(live.linkedSheetId))) {
    await writeQrSettings(spreadsheetId, { ...qr, responsesSheetId: live.linkedSheetId });
    return { responsesSheetId: live.linkedSheetId, createdSheet: false, alreadyLinked: true };
  }

  let responsesSheetId = "";
  if (qr.responsesSheetId && (await verifyFileAccessible(qr.responsesSheetId))) {
    responsesSheetId = qr.responsesSheetId;
  }
  let createdSheet = false;
  if (!responsesSheetId) {
    responsesSheetId = await createResponsesSheet(folderId, `${formTitle} (Responses)`);
    createdSheet = true;
  }
  await writeQrSettings(spreadsheetId, { ...qr, responsesSheetId });
  return { responsesSheetId, createdSheet, alreadyLinked: false };
}

// Creates a standalone responses spreadsheet on demand — used when a reused-by-ID form
// has no responses sheet attached yet. It is NOT attached to the form (MewMaps cannot
// call setDestination, and a reused form is never edited); the user links it in Google
// Forms afterwards (Responses → Link to Sheets). Only ever invoked by an explicit action.
export async function createDetachedResponsesSheet(spreadsheetId: string): Promise<{ responsesSheetId: string }> {
  const qr = await readQrSettings(spreadsheetId);
  if (!qr.formId) throw new FormSyncError("NOT_LINKED", "No form is linked. Create or import one first.");
  const live = await apiGetFormOrNull(qr.formId);
  const formTitle = (live?.info?.title || "Form").trim() || "Form";
  const folderId = await ensureFormsFolder();
  const responsesSheetId = await createResponsesSheet(folderId, `${formTitle} (Responses)`);
  await writeQrSettings(spreadsheetId, { ...qr, responsesSheetId });
  return { responsesSheetId };
}

async function apiSetPublishSettings(formId: string): Promise<unknown> {
  const res = await fetchWithAuth(`${FORMS_BASE}/${formId}:setPublishSettings`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      publishSettings: {
        publishState: { isPublished: true, isAcceptingResponses: true },
      },
      updateMask: "publishState",
    }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    if (res.status === 400 && /publish/i.test(text)) {
      throw new FormSyncError("PUBLISH_UNSUPPORTED", "This form predates Google's publishing model and is already open to responses — no action needed.");
    }
    throw new FormSyncError("PUBLISH_FAILED", `Publish failed (${res.status}): ${text}`);
  }
  return res.json();
}

export async function publishLinkedForm(spreadsheetId: string): Promise<{ formId: string }> {
  const qr = await readQrSettings(spreadsheetId);
  if (!qr.formId) throw new FormSyncError("NOT_LINKED", "No form is linked. Create or import one first.");
  if (qr.formLocked) throw new FormSyncError("FORM_LOCKED", "This form is reused by ID and is never modified by MewMaps. Publish or change it directly in Google Forms.");
  const live = await apiGetFormOrNull(qr.formId);
  if (!live) throw new FormSyncError("FORM_INACCESSIBLE", "Cannot access the linked Google Form. MewMaps can only manage forms it created itself — a form imported by URL must be edited directly in Google Forms.");
  await apiSetPublishSettings(qr.formId);
  return { formId: qr.formId };
}

// Wizard startup: make sure a form exists (link an existing one or create from schema),
// publish it via the Forms API, and ensure a responses spreadsheet exists. The user
// links the responses sheet to the form manually afterwards.
export async function ensureFormOnStart(spreadsheetId: string): Promise<{
  formId: string;
  responsesSheetId: string;
  createdSheet: boolean;
  createdForm: boolean;
  alreadyLinked: boolean;
}> {
  let qr = await readQrSettings(spreadsheetId);
  let createdForm = false;
  if (!qr.formId) {
    if (qr.formUrl) {
      qr = { ...qr, formUrl: "", formUrlBackup: qr.formUrl };
      await writeQrSettings(spreadsheetId, qr);
    }
    try {
      await linkFirstFormInFolder(spreadsheetId);
    } catch (e) {
      if (e instanceof FormSyncError && (e as FormSyncError).code === "NO_FORM_FOUND") {
        await createFormFromSchemaAction(spreadsheetId);
        createdForm = true;
      } else {
        throw e;
      }
    }
  }

  // Publish the form via the Forms API so its link works for users. Skip for forms
  // reused by ID: publishing is an edit, and those forms are already live anyway.
  const qrForPublish = await readQrSettings(spreadsheetId);
  if (qrForPublish.formId && !qrForPublish.formLocked) {
    try {
      await apiSetPublishSettings(qrForPublish.formId);
    } catch (e) {
      if (!(e instanceof FormSyncError) || (e as FormSyncError).code !== "PUBLISH_UNSUPPORTED") {
        console.warn("Form publish warning:", (e as Error).message);
      }
    }
  }

  const sheet = await ensureResponsesSheet(spreadsheetId);
  return {
    formId: qrForPublish.formId,
    responsesSheetId: sheet.responsesSheetId,
    createdSheet: sheet.createdSheet,
    createdForm,
    alreadyLinked: sheet.alreadyLinked,
  };
}

export { SINGLETON_FORM_TYPES };
